/**
 * Decision Stack API Service
 * TanStack Query hooks for decision management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { APIError } from './api';
// import { api } from './api'; // Uncomment when using real API
import type { DecisionCard, SwipeDirection } from '../types/decision';

// Query Keys
export const decisionKeys = {
  all: ['decisions'] as const,
  lists: () => [...decisionKeys.all, 'list'] as const,
  list: (filters?: DecisionFilters) => [...decisionKeys.lists(), filters] as const,
  detail: (id: string) => [...decisionKeys.all, 'detail', id] as const,
  stats: () => [...decisionKeys.all, 'stats'] as const,
};

// Types
export interface DecisionFilters {
  type?: string;
  priority?: string;
  matterId?: string;
  limit?: number;
}

export interface DecisionStats {
  total: number;
  byPriority: Record<string, number>;
  byType: Record<string, number>;
  completedToday: number;
  averageProcessingTime: number;
}

export interface DecisionAction {
  decisionId: string;
  action: 'approve' | 'reject' | 'snooze' | 'delegate';
  direction: SwipeDirection;
  notes?: string;
  delegateTo?: string;
  snoozeUntil?: Date;
}

// API Functions
async function fetchDecisions(filters?: DecisionFilters): Promise<DecisionCard[]> {
  // In production, this would call the real API
  // return api.get<DecisionCard[]>('/decisions', { params: filters });

  // Mock implementation
  await new Promise((resolve) => setTimeout(resolve, 300));

  const mockDecisions: DecisionCard[] = [
    {
      id: '1',
      type: 'invoice',
      priority: 'high',
      title: 'Approve Invoice #2847',
      subtitle: 'Thompson Estate - Final Billing',
      amount: 12450,
      currency: 'USD',
      meta: {
        source: 'Clio',
        createdAt: new Date(),
        deadline: new Date(Date.now() + 86400000 * 2),
        matterName: 'Thompson Estate',
        clientName: 'Sarah Thompson',
        aiConfidence: 0.94,
        suggestedAction: 'approve',
      },
      godModeData: {
        fullDescription: 'Final billing for Thompson Estate probate matter. All time entries verified against retainer agreement.',
        linkedEntities: ['Thompson, James (Deceased)', 'Thompson, Sarah (Beneficiary)'],
        auditTrail: [
          { action: 'Invoice Generated', timestamp: new Date(Date.now() - 86400000), actor: 'System' },
          { action: 'Client Review Sent', timestamp: new Date(Date.now() - 43200000), actor: 'Maria Garcia' },
        ],
        rawData: { invoiceId: 'INV-2847' },
        suggestedResponse: 'Approve - client has pre-approved via email.',
      },
    },
    {
      id: '2',
      type: 'document',
      priority: 'critical',
      title: 'Sign Closing Documents',
      subtitle: 'Riverside LLC Acquisition',
      meta: {
        source: 'DocuSign',
        createdAt: new Date(),
        deadline: new Date(Date.now() + 86400000),
        matterName: 'Riverside Acquisition',
        aiConfidence: 0.89,
      },
      godModeData: {
        fullDescription: 'Asset purchase agreement ready for execution. All conditions met.',
        linkedEntities: ['Riverside LLC', 'Bayshore Investments LLC'],
        auditTrail: [
          { action: 'Documents Prepared', timestamp: new Date(Date.now() - 172800000), actor: 'System' },
        ],
        rawData: { docCount: 12 },
      },
    },
    {
      id: '3',
      type: 'task',
      priority: 'normal',
      title: 'Review Deposition Summary',
      subtitle: 'Martinez v. Acme Corp',
      meta: {
        source: 'Clio',
        createdAt: new Date(Date.now() - 86400000),
        matterName: 'Martinez Litigation',
        aiConfidence: 0.76,
      },
      godModeData: {
        fullDescription: 'AI-generated summary of 6-hour deposition. Key admissions flagged.',
        linkedEntities: ['Martinez, Carlos (Plaintiff)', 'Acme Corp'],
        auditTrail: [
          { action: 'Summary Generated', timestamp: new Date(Date.now() - 86400000), actor: 'Claude' },
        ],
        rawData: { pages: 287 },
      },
    },
    {
      id: '4',
      type: 'email',
      priority: 'high',
      title: 'Reply to Settlement Offer',
      subtitle: 'Martinez v. Acme Corp',
      meta: {
        source: 'Gmail',
        createdAt: new Date(Date.now() - 3600000),
        deadline: new Date(Date.now() + 86400000 * 3),
        matterName: 'Martinez Litigation',
        clientName: 'Carlos Martinez',
        aiConfidence: 0.82,
        suggestedAction: 'approve',
      },
      godModeData: {
        fullDescription: 'Opposing counsel sent settlement offer of $125,000. Previous discussions indicated client minimum of $150,000.',
        linkedEntities: ['Martinez, Carlos', 'Acme Corp', 'John Smith (Opposing Counsel)'],
        auditTrail: [
          { action: 'Email Received', timestamp: new Date(Date.now() - 3600000), actor: 'System' },
          { action: 'AI Analysis Complete', timestamp: new Date(Date.now() - 1800000), actor: 'Claude' },
        ],
        rawData: { emailId: 'msg-123456' },
        suggestedResponse: 'Draft counter-offer at $150,000 with structured payment terms.',
      },
    },
  ];

  // Apply filters
  let filtered = mockDecisions;
  if (filters?.type) {
    filtered = filtered.filter((d) => d.type === filters.type);
  }
  if (filters?.priority) {
    filtered = filtered.filter((d) => d.priority === filters.priority);
  }
  if (filters?.limit) {
    filtered = filtered.slice(0, filters.limit);
  }

  return filtered;
}

async function fetchDecisionStats(): Promise<DecisionStats> {
  // return api.get<DecisionStats>('/decisions/stats');

  await new Promise((resolve) => setTimeout(resolve, 200));

  return {
    total: 12,
    byPriority: { critical: 2, high: 4, normal: 5, low: 1 },
    byType: { invoice: 4, document: 3, task: 3, email: 2 },
    completedToday: 8,
    averageProcessingTime: 45, // seconds
  };
}

async function processDecision(action: DecisionAction): Promise<void> {
  // return api.post('/decisions/process', action);

  await new Promise((resolve) => setTimeout(resolve, 300));

  console.log('Processing decision:', action);

  // Simulate occasional failure for testing optimistic UI rollback
  if (Math.random() < 0.05) {
    throw new APIError('Failed to process decision', 500, 'PROCESSING_ERROR');
  }
}

async function undoDecision(decisionId: string): Promise<DecisionCard> {
  // return api.post<DecisionCard>(`/decisions/${decisionId}/undo`);

  await new Promise((resolve) => setTimeout(resolve, 200));

  // Return the restored decision
  return {
    id: decisionId,
    type: 'task',
    priority: 'normal',
    title: 'Restored Decision',
    subtitle: 'This decision was restored',
    meta: {
      source: 'System',
      createdAt: new Date(),
    },
  };
}

// Hooks

/**
 * Fetch pending decisions
 */
export function useDecisions(filters?: DecisionFilters) {
  return useQuery({
    queryKey: decisionKeys.list(filters),
    queryFn: () => fetchDecisions(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch decision statistics
 */
export function useDecisionStats() {
  return useQuery({
    queryKey: decisionKeys.stats(),
    queryFn: fetchDecisionStats,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Process a decision (approve/reject/snooze)
 * Uses optimistic updates for instant feedback
 */
export function useProcessDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processDecision,

    // Optimistic update
    onMutate: async (action) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: decisionKeys.lists() });

      // Snapshot previous value
      const previousDecisions = queryClient.getQueryData<DecisionCard[]>(decisionKeys.list());

      // Optimistically remove the decision
      queryClient.setQueryData<DecisionCard[]>(decisionKeys.list(), (old) =>
        old?.filter((d) => d.id !== action.decisionId) ?? []
      );

      // Return context for rollback
      return { previousDecisions };
    },

    // Rollback on error
    onError: (_err, _action, context) => {
      if (context?.previousDecisions) {
        queryClient.setQueryData(decisionKeys.list(), context.previousDecisions);
      }
    },

    // Refetch after success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: decisionKeys.stats() });
    },
  });
}

/**
 * Undo a processed decision
 */
export function useUndoDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: undoDecision,

    onSuccess: (restoredDecision) => {
      // Add the decision back to the list
      queryClient.setQueryData<DecisionCard[]>(decisionKeys.list(), (old) =>
        old ? [restoredDecision, ...old] : [restoredDecision]
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: decisionKeys.stats() });
    },
  });
}

/**
 * Prefetch decisions for faster navigation
 */
export function usePrefetchDecisions() {
  const queryClient = useQueryClient();

  return (filters?: DecisionFilters) => {
    queryClient.prefetchQuery({
      queryKey: decisionKeys.list(filters),
      queryFn: () => fetchDecisions(filters),
    });
  };
}

export default {
  useDecisions,
  useDecisionStats,
  useProcessDecision,
  useUndoDecision,
  usePrefetchDecisions,
};
